import { ApiClientError } from '@shopware/api-client'

/*
Usage:

const { recaptcha, isVerifying } = useRecaptchaV3()
let isNotABot = await recaptcha()

if (isNotABot) {
    submitForm()
} else {
    console.error('Recaptcha verification failed')
}
*/

interface RecaptchaResponse {
    success: boolean
    score: number
    challenge_ts?: string
    hostname?: string
    'error-codes'?: string[]
}

interface ApiResponse<T> {
    data: {
        body: T
    }
    status?: number
}

export function useRecaptchaV3() {
    const config = useRuntimeConfig().public
    const { apiClient } = useShopwareContext()
    const isVerifying = ref(false)

    async function recaptcha(): Promise<boolean> {
        // Skip recaptcha in development or test mode
        if (import.meta.dev || config.testMode === 'true') {
            return true
        }

        isVerifying.value = true

        try {
            // Initialize and load Google reCAPTCHA API
            await initRecaptchaApi()

            // Execute reCAPTCHA and get token
            const token = await executeRecaptcha()

            if (!token) {
                return false
            }

            // Verify token with backend
            const response = await apiClient.invoke('verifyRecaptcha post /recaptcha', {
                body: {
                    'g-recaptcha-response': token
                }
            }) as ApiResponse<RecaptchaResponse>

            // Check if verification was successful and score meets threshold
            if (response?.data?.body?.success === true && response?.data?.body?.score >= 0.3) {
                return true
            }

            return false

        } catch (error) {
            if (error instanceof ApiClientError) {
                console.error('reCAPTCHA API error:', error.details)
            } else {
                console.error('reCAPTCHA error:', error)
            }

            // Return false on error to prevent form submission
            return false
        } finally {
            isVerifying.value = false
        }
    }

    async function executeRecaptcha(): Promise<string | null> {
        if (!import.meta.client) {
            return null
        }

        try {
            // @ts-expect-error - grecaptcha is loaded dynamically
            if (!window.grecaptcha) {
                throw new Error('reCAPTCHA not loaded')
            }

            // @ts-expect-error - grecaptcha is loaded dynamically
            const token = await window.grecaptcha.execute(config.googleRecaptchaSiteKey, {
                action: 'submit'
            })

            return token
        } catch (error) {
            console.error('Failed to execute reCAPTCHA:', error)
            return null
        }
    }

    function initRecaptchaApi(): Promise<void> {
        if (!import.meta.client) {
            return Promise.resolve()
        }

        // @ts-expect-error - grecaptcha is loaded dynamically
        if (window.grecaptcha) {
            return Promise.resolve()
        }

        return new Promise((resolve, reject) => {
            // Check if script already exists
            if (document.getElementById('recaptcha-api')) {
                resolve()
                return
            }

            const scriptElement = document.createElement('script')
            scriptElement.type = 'text/javascript'
            scriptElement.src = `https://www.google.com/recaptcha/api.js?render=${config.googleRecaptchaSiteKey}`
            scriptElement.id = 'recaptcha-api'
            scriptElement.async = true
            scriptElement.defer = true

            scriptElement.addEventListener('load', () => {
                // @ts-expect-error - grecaptcha is loaded dynamically
                window.grecaptcha?.ready(() => {
                    resolve()
                })
            })

            scriptElement.addEventListener('error', () => {
                reject(new Error(`Failed to load reCAPTCHA from ${scriptElement.src}`))
            })

            document.head.appendChild(scriptElement)
        })
    }

    return {
        recaptcha,
        initRecaptchaApi,
        isVerifying: computed(() => isVerifying.value)
    }
}
